import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  listStoryIdeas,
  deleteStoryIdea,
  automateStoryIdea,
  publishStoryIdea,
  unpublishStoryIdea,
} from "@/lib/api";

export default function MyStoryIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track which idea is mid-action so we can disable its buttons +
  // show a spinner inline. Keyed by idea_id.
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStoryIdeas();
      setIdeas(res?.ideas || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load saved ideas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const handleAutomate = async (idea) => {
    if (busy[idea.idea_id]) return;
    if (idea.status === "automated") {
      const ok = window.confirm(
        `"${idea.title}" was already automated. Build it again as a new storyboard?`,
      );
      if (!ok) return;
    }
    setBusyFor(idea.idea_id, "automate");
    try {
      const res = await automateStoryIdea(idea.idea_id);
      if (res?.status === "queued") {
        toast.success(
          `Queued behind ${Math.max(0, (res.queue_position || 1) - 1)} other job${res.queue_position === 2 ? "" : "s"} — track progress under Review & Approve`,
        );
      } else {
        toast.success("Sent to the factory — track progress under Review & Approve");
      }
      // Optimistically flip the card to "sent to the factory" so the
      // amber background lights up before load() finishes. Grab the
      // storyboard_id off the response so the Review & Approve link
      // renders straight away — otherwise the card would flash the
      // right state but leave the link empty for the ~500ms it takes
      // the refetch to come back.
      setIdeas((cur) => cur.map((i) => (
        i.idea_id === idea.idea_id
          ? {
              ...i,
              status: "automated",
              storyboard_id: res?.storyboard_id || i.storyboard_id,
              job_id: res?.job_id || i.job_id,
            }
          : i
      )));
      // Refresh so the persisted status matches the optimistic one.
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start Automate");
    } finally {
      setBusyFor(idea.idea_id, null);
    }
  };

  const handleDelete = async (idea) => {
    if (busy[idea.idea_id]) return;
    if (!window.confirm(`Remove "${idea.title}" from My Story Ideas?`)) return;
    setBusyFor(idea.idea_id, "delete");
    try {
      await deleteStoryIdea(idea.idea_id);
      toast.success("Removed from saved ideas");
      setIdeas((cur) => cur.filter((i) => i.idea_id !== idea.idea_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove idea");
    } finally {
      setBusyFor(idea.idea_id, null);
    }
  };

  const handlePublish = async (idea) => {
    if (busy[idea.idea_id]) return;
    // Prompt for the optional YouTube URL. `window.prompt` returns
    // null on cancel (bail out) and "" on OK-with-blank (still mark
    // published — the URL is optional). Any non-cancel value is
    // trimmed + sent through as-is; the backend validates length.
    const raw = window.prompt(
      `Mark "${idea.title}" as published?\n\nPaste the YouTube URL (optional — leave blank if you don't have it handy):`,
      idea.published_url || "",
    );
    if (raw === null) return;
    setBusyFor(idea.idea_id, "publish");
    try {
      const res = await publishStoryIdea(idea.idea_id, raw);
      toast.success("Marked as published");
      setIdeas((cur) => cur.map((i) => (
        i.idea_id === idea.idea_id
          ? {
              ...i,
              published_at: res?.published_at || new Date().toISOString(),
              published_url: res?.published_url ?? ((raw || "").trim() || null),
            }
          : i
      )));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mark published");
    } finally {
      setBusyFor(idea.idea_id, null);
    }
  };

  const handleUnpublish = async (idea) => {
    if (busy[idea.idea_id]) return;
    if (!window.confirm(`Un-mark "${idea.title}" as published?`)) return;
    setBusyFor(idea.idea_id, "publish");
    try {
      await unpublishStoryIdea(idea.idea_id);
      toast.success("Un-marked as published");
      setIdeas((cur) => cur.map((i) => (
        i.idea_id === idea.idea_id
          ? { ...i, published_at: null, published_url: null }
          : i
      )));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't un-mark");
    } finally {
      setBusyFor(idea.idea_id, null);
    }
  };

  // Once an idea's been sent to the factory it lives under Review &
  // Approve — showing it here too clutters the "still needs a
  // decision" queue. Filter automated ideas out of this list; users
  // can still see them (with progress + delete) on Review & Approve.
  // EXCEPTION: an idea marked "published" comes back to the library —
  // it's the user's final trophy shelf and belongs alongside the
  // still-in-progress ideas so they can see the whole catalog here.
  const visible = useMemo(
    () => ideas.filter((i) => i.status !== "automated" || i.published_at),
    [ideas],
  );

  const automatedCount = useMemo(
    () => ideas.filter((i) => i.status === "automated" && !i.published_at).length,
    [ideas],
  );

  const publishedCount = useMemo(
    () => ideas.filter((i) => !!i.published_at).length,
    [ideas],
  );

  return (
    <div className="space-y-6" data-testid="my-story-ideas-page">
      <header className="space-y-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-yellow-400">
          // PHASE 3 · SAVED STORY IDEAS
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Your idea library
        </h1>
        <p className="text-zinc-300 max-w-3xl">
          Bookmark story ideas from{" "}
          <Link to="/generate-ideas" className="text-yellow-300 underline">
            Generate Story Ideas
          </Link>
          {" "}so they survive page refresh, then{" "}
          <span className="text-emerald-300 font-bold">Automate</span> any
          one of them with a single click. Once an idea&apos;s been sent to
          the factory it moves over to{" "}
          <Link to="/review" className="text-yellow-300 underline">
            Review &amp; Approve
          </Link>.
        </p>
        {automatedCount > 0 && (
          <div
            data-testid="my-story-ideas-factory-summary"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-300 border-l-2 border-yellow-400 pl-3 mt-2"
          >
            {automatedCount} idea{automatedCount === 1 ? "" : "s"} sent to the factory ·{" "}
            <Link to="/review" className="underline hover:text-yellow-100">
              track them in Review &amp; Approve →
            </Link>
          </div>
        )}
        {publishedCount > 0 && (
          <div
            data-testid="my-story-ideas-published-summary"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-300 border-l-2 border-emerald-400 pl-3 mt-2"
          >
            {publishedCount} published on YouTube · your trophy shelf ↓
          </div>
        )}
      </header>

      {loading ? (
        <div className="font-mono text-sm text-zinc-400" data-testid="ideas-loading">
          Loading saved ideas…
        </div>
      ) : visible.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="space-y-3" data-testid="story-ideas-list">
          {visible.map((idea) => (
            <IdeaCard
              key={idea.idea_id}
              idea={idea}
              busy={busy[idea.idea_id]}
              onAutomate={() => handleAutomate(idea)}
              onDelete={() => handleDelete(idea)}
              onPublish={() => handlePublish(idea)}
              onUnpublish={() => handleUnpublish(idea)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="ideas-empty"
      className="border-2 border-zinc-800 bg-zinc-950 rounded-sm p-6 font-mono text-sm text-zinc-400 space-y-2"
    >
      <p>Nothing in the ready-to-automate bucket.</p>
      <p>
        Head to{" "}
        <Link to="/generate-ideas" className="text-yellow-300 underline">
          Generate Story Ideas
        </Link>
        {" "}and click the ⭐ <span className="text-yellow-300 font-bold">Save</span> button next to any idea you want to keep. Anything you&apos;ve already sent to the factory lives under{" "}
        <Link to="/review" className="text-yellow-300 underline">Review &amp; Approve</Link>.
      </p>
    </div>
  );
}

function IdeaCard({ idea, busy, onAutomate, onDelete, onPublish, onUnpublish }) {
  const automated = idea.status === "automated";
  const published = !!idea.published_at;
  const tags = Array.isArray(idea.tags) ? idea.tags : [];
  return (
    <li
      data-testid={`story-idea-${idea.idea_id}`}
      className={[
        "border-2 rounded-sm p-4 space-y-2 transition-colors",
        // Published wins visual priority — emerald "trophy shelf" wash.
        // Otherwise Automated gets the amber factory wash. Fresh ideas
        // stay neutral zinc.
        published
          ? "border-emerald-400/70 bg-emerald-400/10 shadow-[inset_0_0_0_1px_rgba(52,211,153,0.15)]"
          : automated
          ? "border-yellow-400/70 bg-yellow-400/10 shadow-[inset_0_0_0_1px_rgba(250,204,21,0.15)]"
          : "border-zinc-800 bg-zinc-950 hover:border-yellow-300/60",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-2 flex-wrap">
            <span>{idea.handle}</span>
            {published && (
              <span
                className="inline-flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-[0.3em] text-black bg-emerald-400 px-2 py-0.5 rounded-sm"
                data-testid={`story-idea-published-badge-${idea.idea_id}`}
                title={`Published${idea.published_at ? " on " + new Date(idea.published_at).toLocaleDateString() : ""}`}
              >
                ✓ Published
              </span>
            )}
            {automated && !published && (
              <span
                className="inline-flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-[0.3em] text-black bg-yellow-400 px-2 py-0.5 rounded-sm"
                data-testid={`story-idea-automated-badge-${idea.idea_id}`}
                title="This idea has been sent to the content factory. Track its progress under Review & Approve."
              >
                ⚙ Sent to the factory
              </span>
            )}
          </div>
          <h3 className="text-lg font-bold text-white leading-snug">
            {idea.title}
          </h3>
          {idea.hook && (
            <p className="text-zinc-300 text-sm italic">{idea.hook}</p>
          )}
          {idea.summary && (
            <p className="text-zinc-400 text-sm leading-relaxed">
              {idea.summary}
            </p>
          )}
          {published && idea.published_url && (
            <a
              href={idea.published_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`story-idea-youtube-link-${idea.idea_id}`}
              className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-300 hover:text-emerald-100 underline break-all"
              title="Open the published video on YouTube"
            >
              ▶ {idea.published_url}
            </a>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {tags.map((t) => (
                <span
                  key={t}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 border border-zinc-700 rounded-sm px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onAutomate}
            disabled={!!busy}
            data-testid={`story-idea-automate-${idea.idea_id}`}
            className={[
              "font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm border-2 transition-colors",
              busy === "automate"
                ? "border-emerald-300 bg-emerald-300/30 text-emerald-100 cursor-wait"
                : automated
                ? "border-zinc-700 text-zinc-400 hover:border-emerald-400 hover:text-emerald-200"
                : "border-emerald-400 text-emerald-200 hover:bg-emerald-400 hover:text-black",
            ].join(" ")}
            title={automated ? "Send this idea to the factory again as a new storyboard" : "Send this idea to the factory now — track progress under Review & Approve"}
          >
            {busy === "automate" ? "Sending…" : automated ? "Re-send to factory" : "Automate"}
          </button>
          <button
            type="button"
            onClick={published ? onUnpublish : onPublish}
            disabled={!!busy}
            data-testid={`story-idea-publish-${idea.idea_id}`}
            className={[
              "font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm border-2 transition-colors",
              busy === "publish"
                ? "border-emerald-300 bg-emerald-300/30 text-emerald-100 cursor-wait"
                : published
                ? "border-emerald-500 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500 hover:text-black"
                : "border-emerald-400 text-emerald-300 hover:bg-emerald-400 hover:text-black",
            ].join(" ")}
            title={published ? "Un-mark as published" : "Mark this idea as published on YouTube (optional URL)"}
          >
            {busy === "publish"
              ? "Saving…"
              : published
              ? "✓ Published · un-mark"
              : "Mark published"}
          </button>
          {automated && (
            <Link
              to="/review"
              data-testid={`story-idea-open-review-${idea.idea_id}`}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-300 hover:text-yellow-100"
              title="Jump to Review & Approve to see progress"
            >
              View in Review & Approve →
            </Link>
          )}
          {automated && idea.storyboard_id && (
            <Link
              to={`/storyboards/${idea.storyboard_id}`}
              data-testid={`story-idea-open-storyboard-${idea.idea_id}`}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-200"
            >
              Open storyboard →
            </Link>
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={!!busy}
            data-testid={`story-idea-delete-${idea.idea_id}`}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-red-400 disabled:opacity-50"
          >
            {busy === "delete" ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </li>
  );
}
